import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import requestStatusCodes from '../../../lib/const/requestStatusCodes';
import { RequestStatuses } from '../../../lib/types/RequestStatuses';
import { Person } from '../../../lib/interfaces/Person';
import {
  activateEmailConfirmationRoute,
  fetchMeUserRoute,
  fullPersonDataRoute,
  getCardCommentsRoute,
  getCardHistoryRoute,
  getPersonAvatarRoute,
  getPersonFilesRoute,
  getPersonPhotoRoute,
  getPersonProfileShortInfoRoute,
  pageableSearchPersonRoute,
  participationRoute,
  personDataRoute,
  searchPersonPOSTRoute,
} from '../../../lib/routes/persons';
import { SearchPersonBody } from '../../../lib/interfaces/SearchPersonBody';
import { MeUser } from '../../../lib/interfaces/MeUser';
import { Comment } from '../../../lib/interfaces/Comment';
import { History } from '../../../lib/interfaces/History';
import { PersonShortProfile } from '../../../lib/interfaces/PersonShortProfile';
import { SearchParamsPersonData } from '../../../lib/interfaces/SearchParamsPersonData';
import { userInfoDescriptionsRoute } from '../../../lib/routes/users';
import { PersonInfoDescriptions } from '../../../lib/interfaces/PersonInfoDescriptions';
import { RootState } from '../../store';

interface InitialState {
  responsibles: Person[];
  responsiblesLoading: RequestStatuses;
  persons: Person[];
  personsLoading: RequestStatuses;
  totalPersons: number;
  meUser?: MeUser;
  meUserLoading: RequestStatuses;
  meUserFull?: Person;
  meUserFullLoading: RequestStatuses;
  personCard: Person | null;
  personCardLoading: RequestStatuses;
  personFullCard: Person | null;
  personFullCardLoading: RequestStatuses;
  personCardComments: Comment[];
  personCardCommentsLoading: RequestStatuses;
  fileHistory: History[];
  fileHistoryLoading: RequestStatuses;
  personPhoto: string[] | null;
  personPhotoLoading: RequestStatuses;
  personProfileShortInfo: PersonShortProfile | null;
  personProfileShortInfoLoading: RequestStatuses;
  participation: {
    wards: any;
    applications: any;
    executors: any;
  };
  participationLoading: RequestStatuses;
  files: File[];
  filesLoading: RequestStatuses;
  activateEmailConfirmationLoading: RequestStatuses;
  activateEmailConfirmationData: string | undefined;
  personAvatar: string | undefined;
  personAvatarLoading: RequestStatuses;
  getPersonInfoDescriptionsLoading: RequestStatuses;
  personInfoDescriptions: PersonInfoDescriptions | null;
  changePersonInfoDescriptionsLoading: RequestStatuses;
}

const initialState: InitialState = {
  responsibles: [],
  responsiblesLoading: requestStatusCodes.idle,
  persons: [],
  personsLoading: requestStatusCodes.idle,
  totalPersons: 0,
  meUserLoading: requestStatusCodes.idle,
  meUserFullLoading: requestStatusCodes.idle,
  personCard: null,
  personCardLoading: requestStatusCodes.idle,
  personFullCard: null,
  personFullCardLoading: requestStatusCodes.idle,
  personCardComments: [],
  personCardCommentsLoading: requestStatusCodes.idle,
  fileHistory: [],
  fileHistoryLoading: requestStatusCodes.idle,
  personPhoto: null,
  personPhotoLoading: requestStatusCodes.idle,
  personProfileShortInfo: null,
  personProfileShortInfoLoading: requestStatusCodes.idle,
  participation: {
    wards: [],
    applications: [],
    executors: [],
  },
  participationLoading: requestStatusCodes.idle,
  files: [],
  filesLoading: requestStatusCodes.idle,
  activateEmailConfirmationLoading: requestStatusCodes.idle,
  activateEmailConfirmationData: undefined,
  personAvatar: undefined,
  personAvatarLoading: requestStatusCodes.idle,
  getPersonInfoDescriptionsLoading: requestStatusCodes.idle,
  personInfoDescriptions: null,
  changePersonInfoDescriptionsLoading: requestStatusCodes.idle,
};

export const getResponsibles = createAsyncThunk(
  'persons/getResponsibles',
  async ({ projectIds, personStatusIds }: SearchPersonBody) => {
    const response = axios.post(searchPersonPOSTRoute(), { projectIds, personStatusIds });
    const { data } = await response;
    return data;
  }
);

const getPersonCard = createAsyncThunk<
  { person: Person },
  { id: number; searchParams?: SearchParamsPersonData },
  { rejectValue: { status: number; message: string } }
>('persons/getPersonCard', async ({ id, searchParams }, { rejectWithValue }) => {
  try {
    const response = axios.get(personDataRoute(id, searchParams));
    const { data } = await response;
    return { person: data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error?.response?.status,
        message: (error?.response?.data as { message: string })?.message || '',
      });
    }
    throw error;
  }
});

const getPersonFullCard = createAsyncThunk<
  { person: Person },
  { id: number },
  { rejectValue: { status: number; message: string } }
>('persons/getPersonFullCard', async ({ id }, { rejectWithValue }) => {
  try {
    const response = axios.get(fullPersonDataRoute(id));
    const { data } = await response;
    return { person: data };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error?.response?.status,
        message: (error?.response?.data as { message: string })?.message || '',
      });
    }
    throw error;
  }
});

const getPersonParticipation = createAsyncThunk('persons/getPersonParticipation', async ({ id }: { id: number }) => {
  const response = axios.get(participationRoute(id));
  const { data } = await response;
  return { person: data };
});

const pageableSearchPersons = createAsyncThunk(
  'persons/pageableSearchPersons',
  async ({ page, search = {}, sort = ['createDate|desc'] }: { page: number; search?: any; sort?: any }) => {
    // @ts-ignore
    const response = axios.post(pageableSearchPersonRoute({ page, size: 10, sort }), search);
    const { data } = await response;
    return { persons: data.content, totalPersons: data.totalElements };
  }
);

export const fetchMeUser = createAsyncThunk('persons/fetchMeUser', async () => {
  const response = axios.get(fetchMeUserRoute());
  const { data } = await response;
  return data;
});

export const fetchMeUserFull = createAsyncThunk(
  'persons/fetchMeUserFull',
  async ({ personId }: { personId: number }) => {
    const response = axios.get(fullPersonDataRoute(personId));
    const { data } = await response;
    return data;
  }
);

export const activateEmailConfirmation = createAsyncThunk(
  'persons/activateEmailConfirmation',
  async ({ token, onSucces }: { token: string; onSucces: () => void }, { rejectWithValue }) => {
    return axios
      .patch(activateEmailConfirmationRoute(token))
      .then((res) => {
        onSucces();
        return res;
      })
      .catch((err) => {
        return rejectWithValue(err.response.data.message);
      });
  }
);

const getCardComments = createAsyncThunk(
  'persons/getCardComments',
  async ({ personId }: { personId: number | string }) => {
    const response = axios.get(getCardCommentsRoute(personId));
    const { data } = await response;
    return data.comments;
  }
);

const getCardHistory = createAsyncThunk(
  'persons/getCardHistory',
  async ({ personId }: { personId: number | string }) => {
    const response = axios.get(getCardHistoryRoute(personId));
    const { data } = await response;
    return data;
  }
);

const getPersonPhoto = createAsyncThunk(
  'persons/getPersonPhoto',
  async ({ personId }: { personId: number | string }) => {
    const response = axios.get(getPersonPhotoRoute(personId));
    const { data } = await response;
    return data;
  }
);

const getPersonProfileShortInfo = createAsyncThunk('persons/getPersonProfileShortInfo', async () => {
  const response = axios.get(getPersonProfileShortInfoRoute());
  const { data } = await response;
  return data;
});

const getPersonAvatar = createAsyncThunk('persons/getPersonAvatar', async (personId: number) => {
  return axios
    .get(getPersonAvatarRoute(personId), {
      responseType: 'arraybuffer',
    })
    .then((res) => {
      return Buffer.from(res.data, 'binary').toString('base64');
    });
});

const getPersonFiles = createAsyncThunk(
  'persons/getPersonFiles',
  async ({ personId }: { personId: number | string }) => {
    const response = axios.get(getPersonFilesRoute(personId));
    const { data } = await response;
    return data;
  }
);

const getPersonInfoDescriptions = createAsyncThunk(
  'persons/getPersonInfoDescriptions',
  async ({ userId }: { userId: number }) => {
    const response = axios.get(userInfoDescriptionsRoute(userId));
    const { data } = await response;
    return data;
  }
);

const changePersonInfoDescriptions = createAsyncThunk(
  'persons/changePersonInfoDescriptions',
  async (
    {
      userId,
      newPersonInfoDescriptions,
    }: {
      userId: number;
      newPersonInfoDescriptions: Partial<PersonInfoDescriptions>;
    },
    { getState }
  ) => {
    const state = getState() as RootState;

    const response = axios.patch(userInfoDescriptionsRoute(userId), {
      ...state.persons.personInfoDescriptions,
      ...newPersonInfoDescriptions,
    });
    const { data } = await response;
    return data;
  }
);

const personsSlice = createSlice({
  name: 'persons',
  initialState,
  reducers: {
    savePersonFullCard: (state, action) => {
      state.personFullCard = action.payload;
    },
    cleanPersonFullCard: (state) => {
      state.personFullCard = null;
    },
    cleanPersonCard: (state) => {
      state.personCard = null;
    },
    setInfoEmailConfirm: (state, { payload }) => {
      if (state.personProfileShortInfo) {
        state.personProfileShortInfo.emailConfirmation = payload;
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getResponsibles.pending, (state) => {
        state.responsiblesLoading = requestStatusCodes.pending;
      })
      .addCase(getResponsibles.rejected, (state) => {
        state.responsiblesLoading = requestStatusCodes.rejected;
      })
      .addCase(getResponsibles.fulfilled, (state, { payload }) => {
        state.responsiblesLoading = requestStatusCodes.fulfilled;
        state.responsibles = payload.content;
      })
      .addCase(pageableSearchPersons.pending, (state) => {
        state.personsLoading = requestStatusCodes.pending;
      })
      .addCase(pageableSearchPersons.rejected, (state) => {
        state.personsLoading = requestStatusCodes.rejected;
      })
      .addCase(
        pageableSearchPersons.fulfilled,
        (state, action: PayloadAction<{ persons: Person[]; totalPersons: number }>) => {
          state.personsLoading = requestStatusCodes.fulfilled;
          state.persons = action.payload.persons;
          state.totalPersons = action.payload.totalPersons;
        }
      )
      .addCase(fetchMeUser.pending, (state) => {
        state.meUserLoading = requestStatusCodes.pending;
      })
      .addCase(fetchMeUser.rejected, (state) => {
        state.meUserLoading = requestStatusCodes.rejected;
      })
      .addCase(fetchMeUser.fulfilled, (state, { payload }) => {
        state.meUserLoading = requestStatusCodes.fulfilled;
        state.meUser = payload;
      })
      .addCase(fetchMeUserFull.pending, (state) => {
        state.meUserFullLoading = requestStatusCodes.pending;
      })
      .addCase(fetchMeUserFull.rejected, (state) => {
        state.meUserFullLoading = requestStatusCodes.rejected;
      })
      .addCase(fetchMeUserFull.fulfilled, (state, { payload }) => {
        state.meUserFullLoading = requestStatusCodes.fulfilled;
        state.meUserFull = payload;
      })
      .addCase(getPersonCard.pending, (state) => {
        state.personCardLoading = requestStatusCodes.pending;
      })
      .addCase(getPersonCard.rejected, (state) => {
        state.personCardLoading = requestStatusCodes.rejected;
      })
      .addCase(getPersonCard.fulfilled, (state, action) => {
        state.personCardLoading = requestStatusCodes.fulfilled;
        state.personCard = action.payload.person;
      })
      .addCase(activateEmailConfirmation.pending, (state) => {
        state.activateEmailConfirmationLoading = requestStatusCodes.pending;
      })
      .addCase(activateEmailConfirmation.rejected, (state, { payload }) => {
        state.activateEmailConfirmationLoading = requestStatusCodes.rejected;
        state.activateEmailConfirmationData = payload as string;
      })
      .addCase(activateEmailConfirmation.fulfilled, (state) => {
        state.activateEmailConfirmationLoading = requestStatusCodes.fulfilled;
        state.activateEmailConfirmationData = 'Спасибо! указанный адрес электронной почты подтверждён.';
      })
      .addCase(getPersonFullCard.pending, (state) => {
        state.personFullCardLoading = requestStatusCodes.pending;
      })
      .addCase(getPersonFullCard.rejected, (state) => {
        state.personFullCardLoading = requestStatusCodes.rejected;
      })
      .addCase(getPersonFullCard.fulfilled, (state, action) => {
        state.personFullCardLoading = requestStatusCodes.fulfilled;
        state.personFullCard = action.payload.person;
      })
      .addCase(getCardComments.pending, (state) => {
        state.personCardCommentsLoading = requestStatusCodes.pending;
      })
      .addCase(getCardComments.rejected, (state) => {
        state.personCardCommentsLoading = requestStatusCodes.rejected;
      })
      .addCase(getCardComments.fulfilled, (state, action) => {
        state.personCardCommentsLoading = requestStatusCodes.fulfilled;
        state.personCardComments = action.payload;
      })
      .addCase(getCardHistory.pending, (state) => {
        state.fileHistoryLoading = requestStatusCodes.pending;
      })
      .addCase(getCardHistory.rejected, (state) => {
        state.fileHistoryLoading = requestStatusCodes.rejected;
      })
      .addCase(getCardHistory.fulfilled, (state, action) => {
        state.fileHistoryLoading = requestStatusCodes.fulfilled;
        state.fileHistory = action.payload;
      })
      .addCase(getPersonPhoto.pending, (state) => {
        state.personPhotoLoading = requestStatusCodes.pending;
      })
      .addCase(getPersonPhoto.rejected, (state) => {
        state.personPhotoLoading = requestStatusCodes.rejected;
      })
      .addCase(getPersonPhoto.fulfilled, (state, action) => {
        state.personPhotoLoading = requestStatusCodes.fulfilled;
        state.personPhoto = action.payload;
      })
      .addCase(getPersonParticipation.pending, (state) => {
        state.participationLoading = requestStatusCodes.pending;
      })
      .addCase(getPersonParticipation.rejected, (state) => {
        state.participationLoading = requestStatusCodes.rejected;
      })
      .addCase(getPersonParticipation.fulfilled, (state, action) => {
        state.participationLoading = requestStatusCodes.fulfilled;
        state.participation = action.payload.person;
      })
      .addCase(getPersonFiles.pending, (state) => {
        state.filesLoading = requestStatusCodes.pending;
      })
      .addCase(getPersonFiles.rejected, (state) => {
        state.filesLoading = requestStatusCodes.rejected;
      })
      .addCase(getPersonFiles.fulfilled, (state, action) => {
        state.filesLoading = requestStatusCodes.fulfilled;
        state.files = action.payload;
      })
      .addCase(getPersonProfileShortInfo.pending, (state) => {
        state.personProfileShortInfoLoading = requestStatusCodes.pending;
      })
      .addCase(getPersonProfileShortInfo.rejected, (state) => {
        state.personProfileShortInfoLoading = requestStatusCodes.rejected;
      })
      .addCase(getPersonProfileShortInfo.fulfilled, (state, action) => {
        state.personProfileShortInfoLoading = requestStatusCodes.fulfilled;
        state.personProfileShortInfo = action.payload;
      })
      .addCase(getPersonAvatar.pending, (state) => {
        state.personAvatarLoading = requestStatusCodes.pending;
      })
      .addCase(getPersonAvatar.rejected, (state) => {
        state.personAvatarLoading = requestStatusCodes.rejected;
      })
      .addCase(getPersonAvatar.fulfilled, (state, action) => {
        state.personAvatarLoading = requestStatusCodes.fulfilled;
        state.personAvatar = action.payload;
      })
      .addCase(getPersonInfoDescriptions.pending, (state) => {
        state.getPersonInfoDescriptionsLoading = requestStatusCodes.pending;
      })
      .addCase(getPersonInfoDescriptions.rejected, (state) => {
        state.getPersonInfoDescriptionsLoading = requestStatusCodes.rejected;
      })
      .addCase(getPersonInfoDescriptions.fulfilled, (state, action) => {
        state.getPersonInfoDescriptionsLoading = requestStatusCodes.fulfilled;
        state.personInfoDescriptions = action.payload;
      })
      .addCase(changePersonInfoDescriptions.pending, (state) => {
        state.changePersonInfoDescriptionsLoading = requestStatusCodes.pending;
      })
      .addCase(changePersonInfoDescriptions.rejected, (state) => {
        state.changePersonInfoDescriptionsLoading = requestStatusCodes.rejected;
      })
      .addCase(changePersonInfoDescriptions.fulfilled, (state, action) => {
        state.changePersonInfoDescriptionsLoading = requestStatusCodes.fulfilled;
        state.personInfoDescriptions = action.payload;
      });
  },
});

const { actions, reducer } = personsSlice;

const { savePersonFullCard, cleanPersonFullCard, cleanPersonCard, setInfoEmailConfirm } = actions;

export {
  reducer,
  pageableSearchPersons,
  getPersonCard,
  getPersonFullCard,
  getCardComments,
  getCardHistory,
  getPersonPhoto,
  getPersonProfileShortInfo,
  savePersonFullCard,
  getPersonParticipation,
  cleanPersonCard,
  cleanPersonFullCard,
  getPersonFiles,
  getPersonAvatar,
  setInfoEmailConfirm,
  getPersonInfoDescriptions,
  changePersonInfoDescriptions,
};
